import {useCallback, useEffect, useRef, useState} from 'react';
import type {ChangeEvent} from 'react';
import {Link} from 'react-router-dom';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {get} from 'lodash';
import Highlighter from 'react-highlight-words';

import {EnrollmentsService} from './EnrollmentsService';
import {
  EnrollmentsPaginatedResponse,
  EnrichedEnrollment,
} from './IEnrollmentsService';

import {useTablePagination} from '../../hooks/useTablePagination';
import type {
  EnrollmentStatus,
  NewUpdateEnrollmentInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';

const {Title, Text} = Typography;
const {TabPane} = Tabs;

const enrollmentsService = new EnrollmentsService();

type EnrollmentTabKey = 'students' | 'classSchools';

const TABLE_SCROLL = {x: '100%'};

const PLACEMENT_STATUS_COLOR: Record<string, string> = {
  DONE: 'success',
  DOING: 'warning',
  TODO: 'error',
};

const ENROLLMENT_STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  EXPIRED: 'red',
};

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.errors?.length) {
    return error.errors.map((item: any) => item.message).join(', ');
  }

  return error?.message || fallback;
};

const getClassSchoolName = (enrollment: EnrichedEnrollment) => {
  const student = enrollment.student as any;
  const classSchool =
    enrollment.classSchool ?? student?.classschool ?? student?.classSchool;

  return (
    classSchool?.name ??
    classSchool?.id ??
    classSchool?._id ??
    enrollment.classId ??
    student?.classschoolId ??
    student?.classSchoolId ??
    '-'
  );
};

const patchEnrollment = (
  response: EnrollmentsPaginatedResponse | undefined,
  updatedEnrollment: Partial<EnrichedEnrollment>,
) => {
  if (!response || !updatedEnrollment?._id) {
    return response;
  }

  return {
    ...response,
    items: response.items.map(item =>
      item._id === updatedEnrollment._id
        ? {
            ...item,
            ...updatedEnrollment,
            student: item.student,
            classSchool: item.classSchool,
          }
        : item,
    ),
  };
};

export const EnrollmentsTable = () => {
  const [activeTab, setActiveTab] = useState<EnrollmentTabKey>('students');
  const [studentEnrollments, setStudentEnrollments] =
    useState<EnrollmentsPaginatedResponse>();
  const [classSchoolEnrollments, setClassSchoolEnrollments] =
    useState<EnrollmentsPaginatedResponse>();
  const [studentError, setStudentError] = useState('');
  const [classSchoolError, setClassSchoolError] = useState('');
  const [isStudentLoading, setIsStudentLoading] = useState(true);
  const [isClassSchoolLoading, setIsClassSchoolLoading] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [studentAppliedSearch, setStudentAppliedSearch] = useState('');
  const [classSchoolSearchInput, setClassSchoolSearchInput] = useState('');
  const [classSchoolAppliedSearch, setClassSchoolAppliedSearch] = useState('');
  const [editingEnrollment, setEditingEnrollment] =
    useState<EnrichedEnrollment>();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isSavingEnrollment, setIsSavingEnrollment] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm] = Form.useForm();

  const {
    current: studentCurrent,
    setCurrent: setStudentCurrent,
    pageSize: studentPageSize,
    total: studentTotal,
    setTotal: setStudentTotal,
    paginationConfig: studentPaginationConfig,
    paginationSelector: studentPaginationSelector,
  } = useTablePagination({itemName: 'student enrollments'});
  const {
    current: classSchoolCurrent,
    setCurrent: setClassSchoolCurrent,
    pageSize: classSchoolPageSize,
    total: classSchoolTotal,
    setTotal: setClassSchoolTotal,
    paginationConfig: classSchoolPaginationConfig,
    paginationSelector: classSchoolPaginationSelector,
  } = useTablePagination({
    itemName: 'class school enrollments',
  });

  const searchInputRef = useRef<any>(null);

  const fetchStudentEnrollments = useCallback(
    (page: number, limit: number, force = false) => {
      setIsStudentLoading(true);
      setStudentError('');
      enrollmentsService
        .getStudentEnrollments(page, limit, force, studentAppliedSearch)
        .then(resp => {
          setStudentEnrollments(resp);
          setStudentTotal(
            studentAppliedSearch
              ? resp.total
              : (resp.classificationTotals?.students ?? resp.total ?? 0),
          );
          setClassSchoolTotal(
            currentTotal =>
              resp.classificationTotals?.classSchools ?? currentTotal,
          );
        })
        .catch(error => {
          setStudentError(
            getErrorMessage(error, 'Failed to load student enrollments.'),
          );
        })
        .finally(() => {
          setIsStudentLoading(false);
        });
    },
    [setClassSchoolTotal, setStudentTotal, studentAppliedSearch],
  );

  const fetchClassSchoolEnrollments = useCallback(
    (page: number, limit: number, force = false) => {
      setIsClassSchoolLoading(true);
      setClassSchoolError('');
      enrollmentsService
        .getClassSchoolEnrollments(page, limit, force, classSchoolAppliedSearch)
        .then(resp => {
          setClassSchoolEnrollments(resp);
          setClassSchoolTotal(
            classSchoolAppliedSearch
              ? resp.total
              : (resp.classificationTotals?.classSchools ?? resp.total ?? 0),
          );
          setStudentTotal(
            currentTotal => resp.classificationTotals?.students ?? currentTotal,
          );
        })
        .catch(error => {
          setClassSchoolError(
            getErrorMessage(error, 'Failed to load class school enrollments.'),
          );
        })
        .finally(() => {
          setIsClassSchoolLoading(false);
        });
    },
    [classSchoolAppliedSearch, setClassSchoolTotal, setStudentTotal],
  );

  useEffect(() => {
    if (activeTab !== 'students') {
      return;
    }

    fetchStudentEnrollments(studentCurrent, studentPageSize);
  }, [activeTab, fetchStudentEnrollments, studentCurrent, studentPageSize]);

  useEffect(() => {
    if (activeTab !== 'classSchools') {
      return;
    }

    fetchClassSchoolEnrollments(classSchoolCurrent, classSchoolPageSize);
  }, [
    activeTab,
    classSchoolCurrent,
    classSchoolPageSize,
    fetchClassSchoolEnrollments,
  ]);

  const handleSearch = (
    selectedKeys: any[],
    confirm: () => void,
    dataIndex: string,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
  };

  const handleServerSearch = (value: string) => {
    const trimmedValue = value.trim();

    if (activeTab === 'students') {
      setStudentSearchInput(trimmedValue);
      setStudentAppliedSearch(trimmedValue);
      setStudentCurrent(1);
      return;
    }

    setClassSchoolSearchInput(trimmedValue);
    setClassSchoolAppliedSearch(trimmedValue);
    setClassSchoolCurrent(1);
  };

  const handleServerSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (activeTab === 'students') {
      setStudentSearchInput(value);
      if (!value) {
        setStudentAppliedSearch('');
        setStudentCurrent(1);
      }
      return;
    }

    setClassSchoolSearchInput(value);
    if (!value) {
      setClassSchoolAppliedSearch('');
      setClassSchoolCurrent(1);
    }
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{padding: 8}} onKeyDown={e => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}>
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{width: 90}}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}} />
    ),
    onFilter: (value: any, record: any) =>
      get(record, dataIndex)
        ?.toString()
        .toLowerCase()
        .includes(value.toLowerCase()) ?? false,
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) setTimeout(() => searchInputRef.current?.select(), 100);
    },
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() ?? ''}
        />
      ) : (
        text
      ),
  });

  const openEditModal = (enrollment: EnrichedEnrollment) => {
    setEditingEnrollment(enrollment);
    setEditError('');
    editForm.setFieldsValue({
      enrollmentStatus: enrollment.enrollmentStatus,
      startDate: enrollment.startDate ? moment(enrollment.startDate) : null,
      endDate: enrollment.endDate ? moment(enrollment.endDate) : null,
    });
    setIsEditModalVisible(true);
  };

  const closeEditModal = () => {
    if (isSavingEnrollment) {
      return;
    }

    setIsEditModalVisible(false);
    setEditingEnrollment(undefined);
    setEditError('');
    editForm.resetFields();
  };

  const saveClassSchoolEnrollment = async () => {
    if (!editingEnrollment?._id) {
      setEditError(
        'Cannot update enrollment because the enrollment id is missing.',
      );
      return;
    }

    try {
      const values = await editForm.validateFields();
      setIsSavingEnrollment(true);
      setEditError('');

      const input: NewUpdateEnrollmentInput = {
        id: editingEnrollment._id,
        enrollmentStatus: values.enrollmentStatus,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
      };

      const updatedEnrollment =
        await enrollmentsService.updateEnrollment(input);

      setClassSchoolEnrollments(previous =>
        patchEnrollment(previous, updatedEnrollment as EnrichedEnrollment),
      );
      setStudentEnrollments(previous =>
        patchEnrollment(previous, updatedEnrollment as EnrichedEnrollment),
      );

      notification.success({
        message: 'Enrollment updated',
        description: 'Class school enrollment changes were saved.',
      });
      closeEditModal();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      const message = getErrorMessage(
        error,
        'Could not update the enrollment. Please try again.',
      );
      setEditError(message);
      notification.error({
        message: 'Update failed',
        description: message,
        duration: 6,
      });
    } finally {
      setIsSavingEnrollment(false);
    }
  };

  const studentColumns: any[] = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      width: 120,
      ...getColumnSearchProps('_id'),
      render: (id: string) => (
        <Tooltip title={id}>
          <Link
            to={`/enrollments/${id}`}
            style={{fontFamily: 'monospace', fontSize: 12}}>
            {id?.slice(-8)}
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Student',
      dataIndex: ['student', 'name'],
      ...getColumnSearchProps('student.name'),
      sorter: (a: any, b: any) =>
        a.student?.name?.localeCompare(b.student?.name),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (_: any, record: EnrichedEnrollment) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.student?.name ?? '-'}</Text>
          <Text type="secondary" style={{fontSize: 11}}>
            {record.student?.id?.split('-')[0]}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      sorter: (a: any, b: any) =>
        moment(a.startDate).unix() - moment(b.startDate).unix(),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (date: string) =>
        date ? (
          <Space direction="vertical" size={0}>
            <Text>{moment(date).format('DD MMM YYYY')}</Text>
            <Text type="secondary" style={{fontSize: 11}}>
              {moment(date).fromNow()}
            </Text>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (date: string) =>
        date ? (
          <Tooltip title={moment(date).format('DD MMM YYYY HH:mm')}>
            <Text>{moment(date).fromNow()}</Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Placement',
      dataIndex: 'placementStatus',
      sorter: (a: any, b: any) =>
        a.placementStatus?.localeCompare(b.placementStatus),
      sortDirections: ['ascend', 'descend', 'ascend'],
      filters: [
        {text: 'TODO', value: 'TODO'},
        {text: 'DOING', value: 'DOING'},
        {text: 'DONE', value: 'DONE'},
      ],
      onFilter: (value: any, record: any) => record.placementStatus === value,
      render: (status: string) => (
        <Tag color={PLACEMENT_STATUS_COLOR[status] ?? 'default'}>
          {status ?? '-'}
        </Tag>
      ),
    },
    {
      title: 'Free?',
      dataIndex: 'isFree',
      width: 80,
      filters: [
        {text: 'Free', value: true},
        {text: 'Paid', value: false},
      ],
      onFilter: (value: any, record: any) => record.isFree === value,
      render: (isFree: boolean) => (
        <Tag color={isFree ? 'green' : 'red'}>{isFree ? 'Free' : 'Paid'}</Tag>
      ),
    },
  ];

  const classSchoolColumns: any[] = [
    {
      title: 'Enrollment ID',
      dataIndex: '_id',
      width: 180,
      ...getColumnSearchProps('_id'),
      render: (id: string) => (
        <Tooltip title={id}>
          <Link
            to={`/enrollments/${id}`}
            style={{fontFamily: 'monospace', fontSize: 12}}>
            {id}
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Class Name',
      key: 'className',
      ...getColumnSearchProps('classSchool.name'),
      render: (_: any, record: EnrichedEnrollment) => (
        <Text strong>{getClassSchoolName(record)}</Text>
      ),
    },
    {
      title: 'Enrollment Status',
      dataIndex: 'enrollmentStatus',
      filters: [
        {text: 'ACTIVE', value: 'ACTIVE'},
        {text: 'EXPIRED', value: 'EXPIRED'},
      ],
      onFilter: (value: any, record: any) => record.enrollmentStatus === value,
      render: (status: string) => (
        <Tag color={ENROLLMENT_STATUS_COLOR[status] ?? 'default'}>
          {status ?? '-'}
        </Tag>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      sorter: (a: any, b: any) =>
        moment(a.startDate).unix() - moment(b.startDate).unix(),
      render: (date: string) =>
        date ? moment(date).format('DD MMM YYYY') : '-',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      sorter: (a: any, b: any) =>
        moment(a.endDate).unix() - moment(b.endDate).unix(),
      render: (date: string) =>
        date ? moment(date).format('DD MMM YYYY') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: EnrichedEnrollment) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
          size="small">
          Edit
        </Button>
      ),
    },
  ];

  const activeLoading =
    activeTab === 'students' ? isStudentLoading : isClassSchoolLoading;
  const activeTotal =
    activeTab === 'students' ? studentTotal : classSchoolTotal;
  const activeSearchValue =
    activeTab === 'students' ? studentSearchInput : classSchoolSearchInput;
  const activeSearchPlaceholder =
    activeTab === 'students' ? 'Search by student ID' : 'Search by class ID';

  return (
    <div style={{padding: '16px 24px'}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}>
        <Title level={3} style={{margin: 0}}>
          Enrollments
          {!activeLoading && activeTotal > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({activeTotal} total)
            </Text>
          )}
        </Title>
        <Space wrap>
          <Input.Search
            allowClear
            enterButton
            value={activeSearchValue}
            placeholder={activeSearchPlaceholder}
            onChange={handleServerSearchChange}
            onSearch={handleServerSearch}
            loading={activeLoading}
            style={{width: 320}}
          />
          {activeTab === 'students'
            ? studentPaginationSelector
            : classSchoolPaginationSelector}
          <Tooltip title="Refresh current tab">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                if (activeTab === 'students') {
                  fetchStudentEnrollments(
                    studentCurrent,
                    studentPageSize,
                    true,
                  );
                } else {
                  fetchClassSchoolEnrollments(
                    classSchoolCurrent,
                    classSchoolPageSize,
                    true,
                  );
                }
              }}
              loading={activeLoading}
            />
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />}>
            <Link to="/enrollments/create" style={{color: 'inherit'}}>
              Create Enrollment
            </Link>
          </Button>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as EnrollmentTabKey)}
        animated>
        <TabPane
          key="students"
          tab={
            <Space size={6}>
              <UserOutlined />
              Student Enrollments
              <Tag>{studentTotal}</Tag>
            </Space>
          }>
          {studentError ? (
            <Alert
              type="error"
              showIcon
              message="Student enrollments failed to load"
              description={studentError}
              action={
                <Button
                  size="small"
                  onClick={() =>
                    fetchStudentEnrollments(
                      studentCurrent,
                      studentPageSize,
                      true,
                    )
                  }>
                  Try again
                </Button>
              }
            />
          ) : (
            <Table
              tableLayout="fixed"
              rowKey="_id"
              columns={studentColumns}
              dataSource={studentEnrollments?.items ?? []}
              loading={isStudentLoading}
              scroll={TABLE_SCROLL}
              pagination={studentPaginationConfig}
              style={{width: '100%'}}
            />
          )}
        </TabPane>
        <TabPane
          key="classSchools"
          tab={
            <Space size={6}>
              <TeamOutlined />
              Class School Enrollments
              <Tag>{classSchoolTotal}</Tag>
            </Space>
          }>
          {classSchoolError ? (
            <Alert
              type="error"
              showIcon
              message="Class school enrollments failed to load"
              description={classSchoolError}
              action={
                <Button
                  size="small"
                  onClick={() =>
                    fetchClassSchoolEnrollments(
                      classSchoolCurrent,
                      classSchoolPageSize,
                      true,
                    )
                  }>
                  Try again
                </Button>
              }
            />
          ) : (
            <Table
              tableLayout="fixed"
              rowKey="_id"
              columns={classSchoolColumns}
              dataSource={classSchoolEnrollments?.items ?? []}
              loading={isClassSchoolLoading}
              scroll={TABLE_SCROLL}
              pagination={classSchoolPaginationConfig}
              style={{width: '100%'}}
            />
          )}
        </TabPane>
      </Tabs>

      <Modal
        title="Edit Class School Enrollment"
        visible={isEditModalVisible}
        onCancel={closeEditModal}
        onOk={saveClassSchoolEnrollment}
        okText="Save changes"
        confirmLoading={isSavingEnrollment}
        destroyOnClose>
        {editError && (
          <Alert
            type="error"
            showIcon
            message="Could not save enrollment"
            description={editError}
            style={{marginBottom: 16}}
          />
        )}
        <Form form={editForm} layout="vertical">
          <Form.Item label="Enrollment ID">
            <Input disabled value={editingEnrollment?._id ?? '-'} />
          </Form.Item>
          <Form.Item label="Class Name">
            <Input
              disabled
              value={
                editingEnrollment ? getClassSchoolName(editingEnrollment) : '-'
              }
            />
          </Form.Item>
          <Form.Item
            name="enrollmentStatus"
            label="Enrollment Status"
            rules={[
              {
                required: true,
                message: 'Please select an enrollment status.',
              },
            ]}>
            <Select placeholder="Select status">
              {(['ACTIVE', 'EXPIRED'] as EnrollmentStatus[]).map(status => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[
              {
                required: true,
                message: 'Please select a start date.',
              },
            ]}>
            <DatePicker style={{width: '100%'}} />
          </Form.Item>
          <Form.Item
            name="endDate"
            label="End Date"
            dependencies={['startDate']}
            rules={[
              ({getFieldValue}) => ({
                validator(_, value) {
                  const startDate = getFieldValue('startDate');

                  if (!value || !startDate || value.isSameOrAfter(startDate)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('End date must be after the start date.'),
                  );
                },
              }),
            ]}>
            <DatePicker style={{width: '100%'}} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
